/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

@Injectable()
export class DownloadService {
    constructor() {}

    public downloadFromUrl(url: string, filename?: string): void {
        const a: HTMLAnchorElement = document.createElement('a');
        a.style.display = 'none';
        document.body.appendChild(a);
        a.href = url;
        a.download = filename;
        setTimeout(() => {
            a.click();
        }, 0);
    }

    public downloadBlob(blob: Blob, filename: string) {
        const a: HTMLAnchorElement = document.createElement('a');
        a.style.display = 'none';
        document.body.appendChild(a);
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        setTimeout(() => {
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
        }, 0);
    }

    public createCSVBlob(data): Blob {
        const csvRows = [];
        data.forEach((row) => {
            const csvItems = [];
            if (Array.isArray(row)) {
                row.forEach((field) => {
                    if (field == null) {
                        field = '';
                    }
                    if (typeof field === 'object') {
                        // probably a detail report
                        if (field.hasOwnProperty('display')) {
                            field = field.display;
                        }
                    }
                    if (typeof field === 'string') {
                        field = field.replace(/"/g, '""');
                        field = `"${field}"`;
                    }
                    csvItems.push(field);
                });
            } else {
                // assume object w/properties
                for (const key in row) {
                    if (row.hasOwnProperty(key)) {
                        let field;
                        field = row[key];
                        if (field == null) {
                            field = '';
                        }
                        field = field.replace(/"/g, '""');
                        field = `"${field}"`;
                        csvItems.push(field);
                    }
                }
            }
            csvRows.push(csvItems.join(','));
        });
        const csvText = csvRows.join('\n');
        return new Blob([csvText], { type: 'text/csv' });
    }

    public createExcelBlob(workbookData) {
        const buf = new ArrayBuffer(workbookData.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== workbookData.length; ++i) {
            view[i] = workbookData.charCodeAt(i) & 0xff;
        }
        return new Blob([view], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }

    /**
     * Converts Base64-encoded dataURL to Blob
     * Adapted from https://github.com/ebidel/filer.js/blob/master/src/filer.js#L137
     * @param dataURL string
     * @returns {Blob}
     */
    public createBlobFromDataURL(dataURL) {
        let parts;
        let contentType;
        let raw;
        const BASE64_MARKER = ';base64,';
        if (dataURL.indexOf(BASE64_MARKER) === -1) {
            parts = dataURL.split(',');
            contentType = parts[0].split(':')[1];
            raw = decodeURIComponent(parts[1]);

            return new Blob([raw], { type: contentType });
        }

        parts = dataURL.split(BASE64_MARKER);
        contentType = parts[0].split(':')[1];
        raw = window.atob(parts[1]);

        const uInt8Array = new Uint8Array(raw.length);

        for (let i = 0; i < raw.length; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
        }
        return new Blob([uInt8Array], { type: contentType });
    }
}
